import {
  Calendar,
  CheckCircle,
  Clock,
  CreditCard,
  X,
  AlertTriangle,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import type { ActiveMembership } from "../../types/membership";
import { useAuth } from "../../context/AuthContext";
import { dictionary } from "../../utils/dictionary";
import { format } from "date-fns";
import { convertToZone } from "../../utils/formatters";
import { useEffect, useState } from "react";
import { PlanChangeModal } from "./PlanChangeModal";
import { FeedbackModal } from "./FeedbackModal";
import { cancelMembership, getCancelStatus, getIsEligibleDowngradeLite, postDowngradeLite } from "../../services/membership/MembershipAxios";
import toast from "react-hot-toast";
import { Alert } from "../common/information/Alert";
import { SwitchToPlanLiteModal } from "./SwitchToPlanLiteModal";
import { CancelPlanModal } from "./CancelPlanModal";
import { CancelChatModal } from "./CancelChatModal";
import config from "@/config/environment";
interface PlanSectionProps {
  plan: ActiveMembership;
}

export function PlanSection({ plan }: PlanSectionProps) {
  const { language } = useAuth();
  const formatDate = "dd/MM/yyyy";
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenCancel, setIsOpenCancel] = useState(false);
  const [isOpenFeedback, setIsOpenFeedback] = useState(false);
  const [cancelStatus, setCancelStatus] = useState<boolean | null>(null);
  const [showSwitchLite, setShowSwitchLite] = useState(false)
  const [isShowSwitchLite, setIsShowSwitchLite] = useState(false)
  const [liteVariant, setLiteVariant] = useState<"discount50" | "regular" | "free">("regular")
  const [isLoadingDowngradeLite, setIsLoadingDowngradeLite] = useState(false);
  const [isOpenCancelChat, setIsOpenCancelChat] = useState(false);
  const [showCancelSuccess, setShowCancelSuccess] = useState(false);

  useEffect(() => {
    if (plan.days_until_membership_expiration) {
      if (plan.days_until_membership_expiration < 7) {
        setLiteVariant("regular");
      } else if (plan.days_until_membership_expiration < 15) {
        setLiteVariant("discount50");
      } else {
        setLiteVariant("free");
      }
    }
  }, [plan]);

  useEffect(() => {
    getCancelStatus()
      .then((res) => {
        setCancelStatus(res.canceled);
      })
      .catch((err) => {
        console.error(err);
      });
  }, []);

  useEffect(() => {
    getIsEligibleDowngradeLite()
      .then((res) => {
        setIsShowSwitchLite(res.eligible);
      })
      .catch((err) => {
        console.error(err);
      });
  }, []);

  const handleUpgrade = () => {
    const whatsappNumber = import.meta.env.VITE_PUBLIC_PHONE_FLUXI;
    const text = dictionary["upgrade-plan"][language];
    const url = `https://api.whatsapp.com/send/?phone=${whatsappNumber}&text=${text}&type=phone_number&app_absent=0`;
    window.open(url, "_blank");
  };

  const handleCancel = () => {
    const env = config.environment;
    if (env === "development") {
      if (!cancelStatus) {
        setIsOpen(false);
        setIsOpenCancelChat(true);
        return;
      }
  
      toast.error(
        dictionary["your-subscription-has-been-canceled"][language]
      );
    }
    if (env === "production") {
      if (!cancelStatus) {
        setIsOpen(false);
        setIsOpenCancel(true);
        return;
      }
      toast.error(
        dictionary["your-subscription-has-been-canceled"][language]
      );
    }
  };

  const handleCancelPlan = () => {
    if (isShowSwitchLite) {
      setIsOpenCancel(false);
      setShowSwitchLite(true);
      return;
    }
    setIsOpenCancel(false);
    setIsOpenFeedback(true);
  };

  const handleCancelFromLite = () => {
    setShowSwitchLite(false);
    setIsShowSwitchLite(false);
    setIsOpenFeedback(true);
  };

  const handleAcceptSwitchLite = () => {
    setIsLoadingDowngradeLite(true);
    postDowngradeLite()
      .then(() => {
        toast.success(dictionary["downgrade-lite-success"][language]);
        setShowSwitchLite(false);
        setIsOpenFeedback(true);
        window.location.reload();
      })
      .catch(() => {
        toast.error(dictionary["downgrade-lite-error"][language]);
      })
      .finally(() => {
        setIsLoadingDowngradeLite(false);
      });
  };

  const handleSupport = () => {
    const whatsappNumber = import.meta.env.VITE_PUBLIC_PHONE_FLUXI;
    const text = dictionary["upgrade-plan"][language];
    const url = `https://api.whatsapp.com/send/?phone=${whatsappNumber}&text=${text}&type=phone_number&app_absent=0`;
    window.open(url, "_blank");
  };

  const handleCanCancel = (lastMessage: string | null) => {
    setIsOpenCancelChat(false);

    const reason = lastMessage || "";
    cancelMembership(reason)
      .then(() => {
        setIsOpenCancel(false);
        setShowSwitchLite(false);
        setIsOpenFeedback(false);
        setIsOpenCancelChat(false);
        setIsShowSwitchLite(false);
        setShowCancelSuccess(true);
      })
      .catch((error) => {
        toast.error(dictionary["error-canceling-subscription"][language]);
        console.error(error);
      });
  };

  const handleDownsell = () => {
    setIsOpenCancelChat(false);
    setShowSwitchLite(true);
  };

  const handleRecovered = () => {
    setIsOpenCancelChat(false);
  };

  return (
    <Card>
      <CardContent className="p-4 md:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4 md:mb-6">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-[#F4F7FE] rounded-xl flex items-center justify-center flex-shrink-0">
              <CreditCard className="w-5 h-5 md:w-6 md:h-6 text-fluxi" />
            </div>
            <div className="flex flex-col gap-2 w-full min-w-0">
              <div className="flex flex-wrap items-center gap-2 md:gap-3">
                <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 break-words">
                  {dictionary[plan?.plan_name as keyof typeof dictionary]?.[language] || plan.plan_name}
                </h2>
                {cancelStatus && (
                    <Alert
                      type="error"
                      icon={X}
                      description="Cancelado"
                      className="rounded border text-xs md:text-sm font-medium w-fit items-center max-w-full sm:max-w-[490px] !px-2 md:!px-4 !py-1 md:!py-2"
                    />
                )}
              </div>

              {!cancelStatus && plan.is_active && (
                <div>
                  <span className="inline-flex items-center px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium bg-[#ecfdf5] text-[#059669] border border-[#a7f3d0]">
                    <CheckCircle className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                    {dictionary["active"][language]}
                  </span>
                </div>
              )}

              {cancelStatus && (
                <Alert
                  type="error"
                  icon={AlertTriangle}
                  description={dictionary["your-access-will-end-on"][language] + convertToZone(
                    format(new Date(plan.payment_ends_at || ""), formatDate),
                    formatDate
                  ) + dictionary["after-this-date-you-will-lose-access-to-fluxi"][language]}
                  className="rounded border text-xs md:text-sm font-medium w-full sm:w-fit items-center max-w-full sm:max-w-[490px] !px-2 md:!px-4 !py-1 md:!py-2"
                />
              )}
            </div>
          </div>
          <button
            onClick={() => setIsOpen(true)}
            className="bg-fluxi hover:bg-fluxi-dark text-white px-3 md:px-4 py-2 rounded-xl text-sm md:text-base whitespace-nowrap flex-shrink-0 w-full sm:w-auto transition-colors"
          >
            {dictionary["change-plan"][language]}
          </button>

          {/* Modals */}
          <SwitchToPlanLiteModal
            isOpen={showSwitchLite}
            onClose={() => setShowSwitchLite(false)}
            onAccept={handleAcceptSwitchLite}
            onCancel={handleCancelFromLite}
            variant={liteVariant}
            isLoading={isLoadingDowngradeLite}
            isLoadingCancel={false}
          />

          <PlanChangeModal
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            onUpgrade={handleUpgrade}
            onCancel={handleCancel}
          />

          <CancelChatModal
            isOpen={isOpenCancelChat}
            onClose={() => setIsOpenCancelChat(false)}
            onCanCancel={handleCanCancel}
            onDownsell={handleDownsell}
            onRecovered={handleRecovered}
          />

          
          <CancelPlanModal
            isOpen={isOpenCancel}
            onClose={() => setIsOpenCancel(false)}
            onSupport={handleSupport}
            onCancel={handleCancelPlan}
          />

          <FeedbackModal
            isOpen={isOpenFeedback}
            onClose={() => setIsOpenFeedback(false)}
          />

          {/* Cancel success modal */}
          {showCancelSuccess && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="absolute inset-0 bg-black/50" />
              <div className="relative bg-white rounded-3xl shadow-lg w-full max-w-md mx-4 p-6">
                <div className="space-y-6">
                  <div className="flex justify-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                      <X className="w-8 h-8 text-red-600" />
                    </div>
                  </div>
                  <div className="text-center space-y-2">
                    <h2 className="text-xl font-semibold text-gray-900">
                      {dictionary["your-subscription-has-been-canceled"]?.[language] || "Tu suscripción ha sido cancelada"}
                    </h2>
                    <p className="text-gray-600">
                      {dictionary["cancel-success-description"]?.[language] || "Tu plan ha sido cancelado. Podrás seguir usando Fluxi hasta el final de tu período de facturación."}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowCancelSuccess(false);
                      window.location.reload();
                    }}
                    className="w-full bg-fluxi hover:bg-fluxi-dark text-white font-semibold py-3 rounded-xl transition-colors"
                  >
                    {dictionary["understood"]?.[language] || "Entendido"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
          <div className="flex items-center space-x-3 md:space-x-4 p-3 md:p-4 bg-gray-50 rounded-xl">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-[#F4F7FE] rounded-xl flex items-center justify-center flex-shrink-0">
              <Calendar className="w-4 h-4 md:w-5 md:h-5 text-fluxi" />
            </div>
            <div className="min-w-0">
              <p className="text-xs md:text-sm text-gray-500">
                {dictionary["activation-date"][language]}
              </p>
              <p className="text-sm md:text-base font-semibold text-gray-900 truncate">
                {convertToZone(
                  format(new Date(plan.inits_at || ""), formatDate),
                  formatDate
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3 md:space-x-4 p-3 md:p-4 bg-gray-50 rounded-xl">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-[#F4F7FE] rounded-xl flex items-center justify-center flex-shrink-0">
              <Clock className="w-4 h-4 md:w-5 md:h-5 text-fluxi" />
            </div>
            <div className="min-w-0">
              <p className="text-xs md:text-sm text-gray-500">
                {dictionary["upcoming-renovation"][language]}
              </p>
              <p className="text-sm md:text-base font-semibold text-gray-900 truncate">
                {convertToZone(
                  format(new Date(plan.payment_ends_at || ""), formatDate),
                  formatDate
                )}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
